import { useMemo } from "react";
import type { TooltipProps } from "recharts";
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import type { Payload as TooltipPayload } from "recharts/types/component/DefaultTooltipContent";
import {
    getSystemFillColor,
    getTooltipCursorStyle,
    OFFSET_BAR_COLOR_ENOVA_YEARLY_CHART,
    OFFSET_LINE_COLOR_ENOVA_YEARLY_CHART,
} from "../../colors";
import {
    AreaSeparator,
    ChartDataTable,
    CustomLegend,
    LegendColorIcon,
    LegendLineIcon,
    TooltipCard,
    TooltipItem,
    XAxisTick,
    YAxisTick,
} from "../../components";
import { TOOLTIP_OFFSET } from "../chart-constants";
import type { EnovaActivityPoint } from "../enova-activity-utils";

const SUPPORT_COUNT_LABEL = "Antall støttet";
const SUPPORT_AMOUNT_LABEL = "Beløp støttet (NOK 1000)";
const SUPPORT_AMOUNT_LABEL_SHORT = "Beløp støttet";
const DEFAULT_SYSTEM_KEY = "Godstransport";
const NOK_TO_THOUSAND = 1 / 1000;
type YearlyChartPoint = {
    year: number;
    count: number;
    amount: number;
};

type YearlyTooltipProps = TooltipProps<number, string> & {
    payload?: TooltipPayload<number, string>[];
};

const formatInteger = (value: number | string) => {
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
        return "0";
    }
    return numericValue.toLocaleString("nb-NO");
};

const getNiceNumber = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) {
        return 0;
    }

    const exponent = Math.floor(Math.log10(value));
    const magnitude = 10 ** exponent;
    const fraction = value / magnitude;

    if (fraction <= 1) {
        return magnitude;
    }
    if (fraction <= 2) {
        return 2 * magnitude;
    }
    if (fraction <= 5) {
        return 5 * magnitude;
    }
    return 10 * magnitude;
};

const buildTickValues = (maxValue: number, desiredTickCount = 5) => {
    const safeMaxValue = Math.max(maxValue, 1);
    const step = getNiceNumber(safeMaxValue / desiredTickCount) || 1;
    const ticks: number[] = [];

    for (let tick = 0; tick <= safeMaxValue + step * 0.01; tick += step) {
        ticks.push(Math.round(tick * 1000) / 1000);
    }

    const lastTick = ticks[ticks.length - 1];
    if (lastTick < safeMaxValue) {
        ticks.push(safeMaxValue);
    }

    return ticks;
};

const EnovaYearlyTooltip: React.FC<YearlyTooltipProps> = ({ active, payload = [] }) => {
    if (!active || payload.length === 0) {
        return null;
    }

    const { year, count, amount } = (payload[0]?.payload || {}) as YearlyChartPoint;

    return (
        <TooltipCard title={year}>
            <TooltipItem label={SUPPORT_COUNT_LABEL} value={count ?? 0} unit="stk." formatValue={formatInteger} />
            <AreaSeparator />
            <TooltipItem
                label={SUPPORT_AMOUNT_LABEL_SHORT}
                value={(amount ?? 0) / NOK_TO_THOUSAND}
                unit="NOK"
                formatValue={formatInteger}
            />
        </TooltipCard>
    );
};

interface EnovaYearlyChartProps {
    legendHeading: string;
    systemKey?: string | null;
    data: EnovaActivityPoint[];
}

const sanitizeYearlyData = (data: EnovaActivityPoint[]): YearlyChartPoint[] =>
    data
        .slice()
        .sort((a, b) => a.year - b.year)
        .map((entry) => ({
            year: entry.year,
            count: Math.max(0, Number(entry.count) || 0),
            amount: Math.max(0, Number(entry.amount) || 0) * NOK_TO_THOUSAND,
        }));

export const EnovaYearlyChart: React.FC<EnovaYearlyChartProps> = ({ legendHeading, systemKey, data }) => {
    const resolvedSystemKey = systemKey ?? DEFAULT_SYSTEM_KEY;
    const barColor = getSystemFillColor(resolvedSystemKey, OFFSET_BAR_COLOR_ENOVA_YEARLY_CHART);
    const lineColor = getSystemFillColor(resolvedSystemKey, OFFSET_LINE_COLOR_ENOVA_YEARLY_CHART);
    const tooltipCursor = getTooltipCursorStyle();
    const chartData = useMemo(() => sanitizeYearlyData(data), [data]);
    const counts = chartData.map(({ count }) => count);
    const amounts = chartData.map(({ amount }) => amount);
    const maxCount = counts.length > 0 ? Math.max(...counts) : 0;
    const maxAmount = amounts.length > 0 ? Math.max(...amounts) : 0;
    const leftDomainMax = Math.max(10, getNiceNumber(maxCount));
    const amountPerCount = leftDomainMax > 0 ? maxAmount / leftDomainMax : 0;
    const amountScale = amountPerCount > 0 ? getNiceNumber(amountPerCount) : 1;
    const rightDomainMax = leftDomainMax * amountScale;
    const leftTicks = buildTickValues(leftDomainMax);
    const rightTicks = leftTicks.map((tick) => tick * amountScale);

    const dataTableColumns = [
        { key: "year", label: "År", getValue: (row: YearlyChartPoint) => String(row.year) },
        {
            key: "count",
            label: SUPPORT_COUNT_LABEL,
            getValue: (row: YearlyChartPoint) => formatInteger(row.count),
        },
        {
            key: "amount",
            label: SUPPORT_AMOUNT_LABEL,
            getValue: (row: YearlyChartPoint) => formatInteger(row.amount),
        },
    ];

    return (
        <>
            <ChartDataTable
                caption={legendHeading}
                columns={dataTableColumns}
                rowKey={(row) => String(row.year)}
                rows={chartData}
            />
            <ResponsiveContainer width="100%" height={600}>
                <ComposedChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="1 1" vertical={false} horizontal={false} opacity={1} />

                    {/* Workaround since the grid lines from CartesianGrid are not rendering correctly */}
                    {leftTicks.map((tick) => (
                        <ReferenceLine
                            key={`grid-${tick}`}
                            y={tick}
                            yAxisId="left"
                            stroke="var(--ds-color-neutral-border-subtle, #d0d5d8)"
                            strokeDasharray="1 1"
                            strokeOpacity={0.7}
                            ifOverflow="extendDomain"
                            zIndex={0}
                        />
                    ))}

                    <XAxis dataKey="year" tick={<XAxisTick />} interval={0} />

                    <YAxis
                        yAxisId="left"
                        tick={<YAxisTick formatValue={formatInteger} />}
                        ticks={leftTicks}
                        domain={[0, leftDomainMax]}
                        allowDecimals={false}
                        label={{
                            value: SUPPORT_COUNT_LABEL,
                            position: { x: 99, y: -10 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                        padding={{ top: 20, bottom: 0 }}
                    />

                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={<YAxisTick formatValue={formatInteger} textAnchor="start" />}
                        ticks={rightTicks}
                        tickMargin={20}
                        domain={[0, rightDomainMax]}
                        allowDecimals={false}
                        label={{
                            value: SUPPORT_AMOUNT_LABEL,
                            position: { x: 60, y: -10 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                        padding={{ top: 20, bottom: 0 }}
                    />

                    <Tooltip content={<EnovaYearlyTooltip />} offset={TOOLTIP_OFFSET} cursor={tooltipCursor} />

                    <Legend
                        verticalAlign="top"
                        wrapperStyle={{ paddingBottom: "40px" }}
                        content={
                            <CustomLegend heading={legendHeading}>
                                <LegendColorIcon label={SUPPORT_COUNT_LABEL} color={barColor} />
                                <LegendLineIcon label={SUPPORT_AMOUNT_LABEL_SHORT} stroke={lineColor} />
                            </CustomLegend>
                        }
                    />

                    <Bar
                        dataKey="count"
                        yAxisId="left"
                        name={SUPPORT_COUNT_LABEL}
                        fill={barColor}
                        maxBarSize={64}
                        zIndex={10}
                    />

                    <Line
                        dataKey="amount"
                        yAxisId="right"
                        name={SUPPORT_AMOUNT_LABEL}
                        type="linear"
                        stroke={lineColor}
                        strokeWidth={3}
                        dot={{
                            r: 4,
                            strokeWidth: 2,
                            stroke: lineColor,
                            fill: "var(--ds-color-surface-default, #fff)",
                        }}
                        zIndex={20}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </>
    );
};
